import { zodResolver } from '@hookform/resolvers/zod';
import { FC, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { VoucherDetails } from '@/api/voucher/voucher.types.ts';
import Button from '@/components/controls/Button.tsx';
import CustomGrid from '@/components/controls/CustomGrid.tsx';
import CustomGridLabelValueRow, {
  LabelValueGridRow,
} from '@/components/controls/CustomGridLabelValueRow.tsx';
import { UpdateVoucherValiditySchema } from '@/components/zod/types';
import {
  convertUnixToDateInputFormat,
  formatDateFromUnix,
} from '@/utils/dateUtil.ts';
import { capitalizeText } from '@/utils/valueFormatter.ts';

type VoucherDetailsEditFormProps = {
  voucherDetails: VoucherDetails;
  onSubmit: (validFor: string) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  isAdmin?: boolean;
};

type UpdateVoucherValidityFormData = z.infer<
  typeof UpdateVoucherValiditySchema
>;

const VoucherDetailsEditForm: FC<VoucherDetailsEditFormProps> = ({
  voucherDetails,
  onSubmit,
  onCancel,
  isLoading,
  isAdmin = false,
}: VoucherDetailsEditFormProps) => {
  const currentValidFor = convertUnixToDateInputFormat(
    voucherDetails.validFromUnix
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<UpdateVoucherValidityFormData>({
    resolver: zodResolver(UpdateVoucherValiditySchema),
    defaultValues: {
      validFor: currentValidFor,
    },
    mode: 'onChange',
  });

  const processSubmit = async (
    data: UpdateVoucherValidityFormData
  ): Promise<void> => {
    await onSubmit(data.validFor);
  };

  const staticGridData: LabelValueGridRow[] = [
    {
      id: 'name',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Event Name:',
      labelStyle: 'w-1/3',
      value: voucherDetails.name,
      valueStyle: 'w-2/3',
    },
    {
      id: 'code',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Code:',
      labelStyle: 'w-1/3',
      value: voucherDetails.code,
      valueStyle: 'w-2/4',
    },
    {
      id: 'status',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Status:',
      labelStyle: 'w-1/3',
      value: voucherDetails.status,
      transform: capitalizeText,
      valueStyle: 'w-2/3 text-lg font-bold',
    },
    ...(isAdmin
      ? [
          {
            id: 'companyName',
            rowStyle: 'col-span-1 flex flex-row',
            label: 'Issued by:',
            labelStyle: 'w-1/3',
            value: voucherDetails.companyName ?? '',
            valueStyle: 'w-2/3',
            noValueText: 'N/A',
          },
          {
            id: 'companyEmail',
            rowStyle: 'col-span-1 flex flex-row',
            label: 'Email Address:',
            labelStyle: 'w-1/3',
            value: voucherDetails.companyEmail ?? '',
            valueStyle: 'w-2/3 break-all',
            noValueText: 'N/A',
          },
        ]
      : []),
    {
      id: 'type',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Type:',
      labelStyle: 'w-1/3',
      value: voucherDetails.type,
      transform: capitalizeText,
      valueStyle: 'w-2/3',
    },
    {
      id: 'dateCreated',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Date Created:',
      labelStyle: 'w-1/3',
      value: voucherDetails.timeOfPurchaseUnix,
      transform: formatDateFromUnix,
      valueStyle: 'w-2/3',
    },
    {
      id: 'rewardTierPrize',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Reward Tier:',
      labelStyle: 'w-1/3',
      value: `R${voucherDetails.rewardTierPrize}`,
      valueStyle: 'w-2/3',
    },
  ];

  const staticGridItems: ReactNode[] = staticGridData.map((item) => (
    <CustomGridLabelValueRow
      key={item.id}
      id={item.id}
      rowStyle={item.rowStyle}
      label={item.label}
      labelStyle={item.labelStyle}
      value={item.transform ? item.transform(item.value) : item.value}
      noValueText={item.noValueText}
      valueStyle={item.valueStyle}
    />
  ));

  return (
    <form onSubmit={handleSubmit(processSubmit)}>
      <CustomGrid
        items={staticGridItems}
        cols={1}
        gap={4}
        className="mt-4 mb-4"
      />

      <label
        htmlFor="validFor"
        className="flex flex-col sm:flex-row sm:items-center mb-4 gap-2"
      >
        <span className="w-full sm:w-1/3 text-gray-700">Event Date:</span>
        <div className="w-full sm:w-2/3">
          <input
            id="validFor"
            type="date"
            {...register('validFor')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
            disabled={isLoading}
          />
          {errors.validFor ? (
            <p className="text-red-500 text-sm mt-1">
              {errors.validFor.message}
            </p>
          ) : null}
        </div>
      </label>

      <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
        <Button
          buttonStyle="justify-center items-center px-5 py-2 text-base font-semibold text-white whitespace-nowrap bg-primary-100 hover:bg-primary-200 rounded-lg w-full sm:w-auto"
          text="Submit"
          buttonType={{ type: 'submit' }}
          disabled={isLoading || !isDirty}
          hasImage={false}
        />
        <Button
          buttonStyle="justify-center items-center px-5 py-2 text-base font-semibold border border-primary-100 text-primary-100 whitespace-nowrap bg-inherit hover:bg-gray-100 rounded-lg w-full sm:w-auto"
          buttonType={{ type: 'button' }}
          text="Cancel"
          onClick={onCancel}
          disabled={isLoading}
          hasImage={false}
        />
      </div>
    </form>
  );
};

export default VoucherDetailsEditForm;
